import { Injectable } from '@angular/core';

type ScheduleSlot = {
  dateTimeStart: string;
  dateTimeEnd: string;
};

@Injectable({
  providedIn: 'root'
})
export class ScheduleService {

  constructor() { }

  setDateTimesFromWeeklyRecurrenceAndDateInterval(config: {
    dateIntervalStart: string | Date,
    dateIntervalEnd: string | Date,
    chosenWeekDay: string,
    timeStart: string | Date,
    timeEnd: string | Date
  }): ScheduleSlot[] {
    // Generates a list of start/end datetimes for a *weekly* recurrence.
    // Example: every Monday between 2026-02-01 and 2026-02-28 from 09:00 to 17:00.
    //
    // Important timezone rule for scheduling UX:
    // - Users pick *local wall-clock times* (e.g. 09:00) for a given weekday.
    // - We store FHIR instants as ISO strings (UTC via toISOString()).
    // That means we must construct the datetime in *local time* first, then serialize to UTC.

    const startDateLocal = this.parseDateToLocalMidnightDate(config.dateIntervalStart);
    const endDateLocal = this.parseDateToLocalMidnightDate(config.dateIntervalEnd);
    if (!startDateLocal || !endDateLocal) return [];
    if (endDateLocal.getTime() < startDateLocal.getTime()) return [];

    const chosenDayIndex = this.parseWeekdayToIndex(config.chosenWeekDay);
    if (chosenDayIndex === null) return [];

    const startTime = this.parseTimeToHoursMinutes(config.timeStart);
    const endTime = this.parseTimeToHoursMinutes(config.timeEnd);
    if (!startTime || !endTime) return [];

    const slots: ScheduleSlot[] = [];

    // Walk the date interval using local calendar days.
    // Use Date#setDate(+1) instead of adding 24h ms to avoid DST issues.
    const cursor = new Date(startDateLocal.getTime());
    while (cursor.getTime() <= endDateLocal.getTime()) {
      if (cursor.getDay() === chosenDayIndex) {
        console.log(`[ScheduleService] MATCH FOUND: ${cursor.toDateString()}`);
        const start = new Date(
          cursor.getFullYear(),
          cursor.getMonth(),
          cursor.getDate(),
          startTime.hours,
          startTime.minutes,
          0,
          0
        );

        const crossesMidnight = (endTime.hours * 60 + endTime.minutes) <= (startTime.hours * 60 + startTime.minutes);
        const endBase = new Date(cursor.getTime());
        if (crossesMidnight) endBase.setDate(endBase.getDate() + 1);

        const end = new Date(
          endBase.getFullYear(),
          endBase.getMonth(),
          endBase.getDate(),
          endTime.hours,
          endTime.minutes,
          0,
          0
        );

        slots.push({
          dateTimeStart: start.toISOString(),
          dateTimeEnd: end.toISOString(),
        });
      }

      cursor.setDate(cursor.getDate() + 1);
    }

    return slots;
  }
  checkForConflicts(otherSlots: {
    dateTimeStart: string,
    dateTimeEnd: string,

  }[], newSlot: Partial<ScheduleSlot>): { hasConflict: boolean; conflicts: ScheduleSlot[] } {
    // Checks whether a new slot overlaps any of the existing slots.
    // Overlap rule (half-open intervals):
    // - [A.start, A.end) overlaps [B.start, B.end) iff:
    //   A.start < B.end AND B.start < A.end

    const newStart = newSlot.dateTimeStart ? new Date(newSlot.dateTimeStart) : null;
    const newEnd = newSlot.dateTimeEnd ? new Date(newSlot.dateTimeEnd) : null;
    if (!newStart || !newEnd || Number.isNaN(newStart.getTime()) || Number.isNaN(newEnd.getTime())) {
      return { hasConflict: false, conflicts: [] };
    }

    if (newEnd.getTime() <= newStart.getTime()) {
      // Invalid slot; treat as non-conflicting (caller should validate separately).
      return { hasConflict: false, conflicts: [] };
    }

    const conflicts: ScheduleSlot[] = otherSlots
      .map((s) => ({ dateTimeStart: s.dateTimeStart, dateTimeEnd: s.dateTimeEnd }))
      .filter((slot) => {
        const existingStart = new Date(slot.dateTimeStart);
        const existingEnd = new Date(slot.dateTimeEnd);
        if (Number.isNaN(existingStart.getTime()) || Number.isNaN(existingEnd.getTime())) return false;
        if (existingEnd.getTime() <= existingStart.getTime()) return false;

        return newStart.getTime() < existingEnd.getTime() && existingStart.getTime() < newEnd.getTime();
      });

    return { hasConflict: conflicts.length > 0, conflicts };
  }

  private parseWeekdayToIndex(chosenWeekDay: string): number | null {
    // Accepts: "Monday", "mon", "MONDAY", etc.
    // Returns JS weekday index where Sunday=0 ... Saturday=6.
    const key = (chosenWeekDay ?? '').trim().toLowerCase();
    if (!key) return null;

    const map: Record<string, number> = {
      sunday: 0, sun: 0,
      monday: 1, mon: 1,
      tuesday: 2, tue: 2, tues: 2,
      wednesday: 3, wed: 3,
      thursday: 4, thu: 4, thur: 4, thurs: 4,
      friday: 5, fri: 5,
      saturday: 6, sat: 6,
    };

    return map[key] ?? null;
  }

  private parseDateToLocalMidnightDate(input: string | Date): Date | null {
    // Converts an input date into a local-midnight Date for the same calendar day.
    // This matches how users think about recurrence: "every Monday between these dates".
    if (input instanceof Date) {
      if (Number.isNaN(input.getTime())) return null;
      return new Date(input.getFullYear(), input.getMonth(), input.getDate());
    }

    const raw = (input ?? '').trim();
    if (!raw) return null;

    // Prefer YYYY-MM-DD parsing.
    const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})/.exec(raw);
    if (m) {
      const year = Number(m[1]);
      const monthIndex = Number(m[2]) - 1;
      const day = Number(m[3]);
      return new Date(year, monthIndex, day);
    }

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private parseTimeToHoursMinutes(input: string | Date): { hours: number; minutes: number } | null {
    // Accepts "HH:mm" (recommended) or a Date-like string.
    // Also supports Date objects (e.g. Material timepicker output).
    if (input instanceof Date) {
      if (Number.isNaN(input.getTime())) return null;
      return { hours: input.getHours(), minutes: input.getMinutes() };
    }

    const raw = (input ?? '').trim();
    if (!raw) return null;

    const hm = /^([0-9]{1,2}):([0-9]{2})$/.exec(raw);
    if (hm) {
      const hours = Number(hm[1]);
      const minutes = Number(hm[2]);
      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
      return { hours, minutes };
    }

    // Fallback: parse as Date and extract local time.
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return { hours: d.getHours(), minutes: d.getMinutes() };
  }


}
